// @flow
import type { Dispatch } from "redux"
import Cookies from "js-cookie"

import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import { getTimeFromAppInitialized } from "common/utils/perfTracker"
import type { Splitster } from "common/types/splitster"
import { RESERVATION_MODALS } from "booking/scenes/Modals/consts/modalsConsts"
import { openModal } from "../../modals/actions"
import { setSessionInvalid } from "../../states/actions"
import { totalValues, triggerPriceChangeModal, PRICE_CHANGE } from "../../priceChange/actions"
import resolvePriceChange from "../helpers/resolvePriceChange"

const onAcceptableDeviation = (newPrice, diff) => {
  cuckoo.track(cuckooEvents.BOOKING_PRICE_CHANGE_ACCEPTABLE_DEVIATION, {
    diff,
  })
}

const onCheaper = (newPrice, diff) => {
  cuckoo.track(cuckooEvents.BOOKING_PRICE_CHANGE_DECREASE, { diff })
}

const getUsedPriceLoad = ({
  doPriceCheck,
  originalPrice,
  resPrice,
  splitster,
  onMoreExpensive,
}) => {
  if (!doPriceCheck || !originalPrice) {
    return resPrice
  }
  if (Cookies.get(PRICE_CHANGE) || splitster.get("PRICE_CHANGES_DELAY").value === "old")
    return resolvePriceChange(originalPrice, resPrice, {
      onAcceptableDeviation,
      onMoreExpensive,
      onCheaper,
    })
  return originalPrice
}

const getUsedPriceUpdate = ({
  doPriceCheck,
  originalPrice,
  resPrice,
  onMoreExpensive,
  splisterValue,
}) => {
  if (!originalPrice) return resPrice

  if (doPriceCheck || splisterValue) {
    return resolvePriceChange(originalPrice, resPrice, {
      onAcceptableDeviation,
      onMoreExpensive,
      onCheaper,
      preferOriginalPriceWhenCheaper: true,
    })
  }
  if (!doPriceCheck && originalPrice < resPrice) {
    return originalPrice
  }
  return resPrice
}

type Input = {
  context: "load" | "update",
  res: any,
  originalPrice: number,
  passengersCount: number,
  splitster: Splitster,
  state: any,
  dispatch: Dispatch<any>,
}

const priceChangeCheck = ({
  context,
  res,
  originalPrice,
  passengersCount,
  splitster,
  state,
  dispatch,
}: Input) => {
  let doPriceCheck = true // eslint-disable-line fp/no-let

  if (res.get("flightsInvalid")) {
    doPriceCheck = false

    if (context === "load") {
      cuckoo.track(cuckooEvents.BOOKING_FLIGHT_INVALID_INITIAL_TRIGGERED)
      cuckoo.infinario("InvalidFlight", { action: "happened" })
    } else if (context === "update") {
      cuckoo.track(cuckooEvents.BOOKING_FLIGHT_INVALID_UPDATE_TRIGGERED)
    }

    dispatch(openModal(RESERVATION_MODALS.ERROR.INVALID, { passengersCount }))
    dispatch(setSessionInvalid())
    if (context === "update") {
      clearInterval(window.SHAME_BOOKING_UPDATE_INTERVAL)
    }
  }

  if (
    context === "update" &&
    splitster.get("PRICE_CHANGES_DELAY").value === "new" &&
    totalValues(state) < 2 &&
    getTimeFromAppInitialized() < 1000 * 60 * 20
  ) {
    doPriceCheck = false
  }

  const resPrice = res.get("basePrice")

  const onMoreExpensive = (newPrice, diff) => {
    cuckoo.gtm("RESERVATION_PRICE_CHANGE", { priceChange: diff })
    if (context === "load") {
      cuckoo.track(cuckooEvents.BOOKING_PRICE_CHANGE_INCREASE_INITIAL_TRIGGERED, { diff })
    }
    if (context === "update") {
      cuckoo.track(cuckooEvents.BOOKING_PRICE_CHANGE_INCREASE_UPDATE_TRIGGERED, { diff })
    }
    cuckoo.screenRecording("price_change", "happened")
    dispatch(
      triggerPriceChangeModal(
        {
          originalPrice,
          resPrice,
          passengersCount,
        },
        splitster.get("PRICE_CHANGES_DELAY").value === "new",
      ),
    )
  }

  // Load
  if (context === "load") {
    return getUsedPriceLoad({
      doPriceCheck,
      originalPrice,
      resPrice,
      splitster,
      onMoreExpensive,
    })
  }
  // Update
  return getUsedPriceUpdate({
    doPriceCheck,
    originalPrice,
    resPrice,
    onMoreExpensive,
    splisterValue: splitster.get("PRICE_CHANGES_DELAY").value === "old",
  })
}

export default priceChangeCheck
